import {
  EMessengerEventBusEvents,
  EPinnedMessagesEventBusEvents,
  IPinnedMessagesEventBusOpenPayload,
  PinnedMessagesEventBus,
} from '@xometry/ui';
import { CURRENT_REGION } from 'config/regions/config';
import { getForeignObjectLink } from 'helpers/deal/getDealObjectLink';
import { useCallback, useEffect, useState } from 'react';
import { useStore } from 'stores/RootStore';
import { ESide, ESideOverlayType } from 'stores/SideOverlay/SideOverlayStore';
import { withMessengerReinitLayoutMode } from 'utils/messenger/withMessengerReinitLayoutMode';

import { DeviceTypes, useDeviceType } from './useDeviceType';
import { useEmbedded } from './useEmbedded';
import { useMessengerEventBus } from './useMessengerEventBus';

export const usePinnedMessagesEventBus = () => {
  const { sideOverlayStore, erpMessengerLayoutStore } = useStore();
  const { openSideOverlay, context, clearMessengerLayoutPresentationOverride } = sideOverlayStore;

  const { sendEvent } = useMessengerEventBus();
  const { isEmbedded } = useEmbedded();
  const deviceType = useDeviceType();

  const isMobile = deviceType === DeviceTypes.MOBILE && !isEmbedded;

  const [pinnedMessagesEventBus, setPinnedMessagesEventBus] = useState<PinnedMessagesEventBus>();

  const handlePinnedMessageClick = useCallback(
    ({ initialMessageId, sourceType, dealId, sourceId, parentId, region }: IPinnedMessagesEventBusOpenPayload) => {
      if (region !== CURRENT_REGION) {
        const objectLink = getForeignObjectLink({
          region,
          type: sourceType,
          dealId,
          objectId: sourceId,
          parentId,
        });
        const hash = `#messenger=Chat&initialMessageId=${initialMessageId}`;

        if (objectLink) {
          const link = `${objectLink}${hash}`;
          window.open(link, '_blank');

          return;
        }
      }

      if (context.type === ESideOverlayType.Messenger) {
        clearMessengerLayoutPresentationOverride();

        sendEvent?.(
          EMessengerEventBusEvents.REINITIALIZE_MC,
          withMessengerReinitLayoutMode({
            userPreference: erpMessengerLayoutStore.userErpMessengerLayoutMode,
            isMobile,
            isEmbedded,
            overlayContext: sideOverlayStore.context,
            payload: {
              initialMessageId,
            },
          }),
        );
      } else {
        openSideOverlay(
          {
            type: ESideOverlayType.Messenger,
            initialMessageId,
          },
          ESide.Left,
        );
      }
    },
    [
      context.type,
      erpMessengerLayoutStore.userErpMessengerLayoutMode,
      isEmbedded,
      isMobile,
      openSideOverlay,
      sendEvent,
      clearMessengerLayoutPresentationOverride,
      sideOverlayStore,
    ],
  );

  useEffect(() => {
    if (pinnedMessagesEventBus) {
      pinnedMessagesEventBus.addEventListener(
        EPinnedMessagesEventBusEvents.OPEN_MESSENGER_WITH_MESSAGE,
        handlePinnedMessageClick,
      );

      return () => {
        pinnedMessagesEventBus.removeEventListener(
          EPinnedMessagesEventBusEvents.OPEN_MESSENGER_WITH_MESSAGE,
          handlePinnedMessageClick,
        );
      };
    }
  }, [pinnedMessagesEventBus, handlePinnedMessageClick]);

  const setEventBusRef = useCallback((eventBus: PinnedMessagesEventBus) => {
    setPinnedMessagesEventBus(eventBus);
  }, []);

  return {
    setEventBusRef,
  };
};
